/**
 * The WhatsApp escape hatch: a buyer who is not ready to fill in a brief can
 * reach a designer in one tap instead of bouncing.
 *
 * One number, one label, one normaliser — worded once here, the way
 * `assurances.ts` holds the booking promise. Before this, the same phone
 * formatter was copy-pasted into `RequestCollabModal` and `ProductDetails`,
 * each with its own fallback, so none of them could be fixed in one place.
 *
 * Two rules the whole app follows:
 *
 *   • **The designer's own number when they have one, ours when they don't.**
 *     `formatWhatsAppPhone` falls back to AnkaraGrid Africa, so a buyer who
 *     taps a creator with no number saved still reaches a human.
 *   • **The prefill is a greeting, nothing more.** A paragraph written in the
 *     buyer's name gets deleted before it is sent, and then the context it was
 *     carrying is lost anyway. What the conversation is about is recorded on
 *     our side instead — see `OUTREACH_NOTES` — so the designer sees it in
 *     their dashboard whatever the buyer types.
 *
 * Where it belongs:
 *   • Discovery surfaces (home, archive, edits, designers) — the floating pill.
 *   • A designer's profile, a dress or a look — beside the primary CTA, never
 *     instead of it.
 *   • Never on the order, payment or messaging routes. Once money is involved
 *     the order chat is the record buyer protection relies on — see
 *     `pages/help/articles/StayingSafe.tsx`.
 */

/** AnkaraGrid Africa's own line. Digits only — wa.me rejects '+' and spaces. */
export const WHATSAPP_NUMBER = '2348139094700';

/** Default label. Used on the floating pill and anywhere with room for it. */
export const TALK_TO_DESIGNER = 'Talk to a designer';

/** When a specific piece is on screen, name it. The question is about that dress. */
export const TALK_TO_DESIGNER_DRESS = 'Talk to a designer about this dress';

/** The prefill when the tap reaches AnkaraGrid rather than a named designer. */
export const WHATSAPP_GREETING =
  'Hi Ankaragrid. I would like to talk to a fashion designer about a piece I have in mind.';

/**
 * The prefill, addressed to whoever receives it.
 *
 * A buyer tapping through from a designer's profile or their dress reaches
 * that designer's own WhatsApp, so greeting AnkaraGrid there would read as a
 * mis-sent message. Named designer, named greeting; everyone else gets the
 * line above.
 */
export const greetingFor = (designerName?: string | null, reachesFallback = true) =>
  !reachesFallback && designerName?.trim()
    ? `Hi ${designerName.trim()}. I would like to talk to you about a piece I have in mind.`
    : WHATSAPP_GREETING;

/**
 * What gets recorded against the request, not what gets typed into WhatsApp.
 * These reach the designer through `collaboration/requests`, so they read as a
 * note about the buyer rather than a message from them.
 */
export const OUTREACH_NOTES = {
  general: 'Wants to talk to a designer about a piece they have in mind',
  dress: (title?: string) =>
    `Asking about ${title ? `"${title}"` : 'a dress'} — fabric, colour and fit before booking`,
  edit: (editName?: string) =>
    `Browsing ${editName ? `the ${editName} edit` : 'the edits'}, wants help choosing a piece`,
  profile: 'Reached out from the designer profile',
  brief: 'Filling in a custom brief, would rather talk it through',
  briefSent: 'Has sent a brief and wants to talk it through',
} as const;

/**
 * Digits for `wa.me`, with AnkaraGrid Africa as the fallback.
 *
 * Lifted verbatim from the formatter that `RequestCollabModal` and
 * `ProductDetails` each carried: strip everything that is not a digit, and
 * read a leading 0 on an 11-digit number as Nigerian. Other formats pass
 * through as typed, because guessing a country code is worse than not.
 */
export const formatWhatsAppPhone = (phone?: string | null): string => {
  const cleaned = (phone ?? '').replace(/\D/g, '');
  if (!cleaned) return WHATSAPP_NUMBER;
  if (cleaned.startsWith('0') && cleaned.length === 11) return `234${cleaned.substring(1)}`;
  return cleaned;
};

/** Builds the deep link. Keep every `wa.me` URL in the app going through this. */
export const whatsAppUrl = (message: string = WHATSAPP_GREETING, phone?: string | null) =>
  `https://wa.me/${formatWhatsAppPhone(phone)}?text=${encodeURIComponent(message)}`;

/** True when the tap will reach us rather than the creator. */
export const isFallbackNumber = (phone?: string | null) =>
  formatWhatsAppPhone(phone) === WHATSAPP_NUMBER;
